import React, { useCallback, useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { useThemeMode } from '../../design-system/theme';
import '../../pages/Checkout/CheckoutTheme.css';
import { Spinner } from 'react-bootstrap';
import './HostedCheckout.css';
import { capturePostHogEvent, trackBeginCheckout } from '../../utils/analytics';
import { createCheckoutAttemptId } from '../../utils/checkoutAttemptId';
import {
  createHostedCheckoutSession,
  HOSTED_CHECKOUT_FEATURE_FLAG,
  HostedCheckoutError,
  isStripeCheckoutUrl,
  type HostedCheckoutItem,
} from '../../utils/hostedCheckout';

type Props = {
  items: HostedCheckoutItem[];
  promoCode?: string | null;
  /**
   * The buyer tapped "Have a different promo code? Enter it here" on the
   * Stripe page. Stripe hides its own box while a sale is pre-applied, so send
   * them straight back to Stripe with its own promo box (Rob, 2026-09-29: no
   * separate BotSpot promo page).
   */
  promoEntry?: boolean;
  returnContext: 'subscriptions' | 'signup_survey' | 'ai_agent';
  sourceRoute: string;
  flowContext?: string | null;
  surface: string;
  assignment: {
    assignmentId: string;
    subjectId: string;
    exposedAt: string;
  };
  getApiToken?: (() => Promise<string | null>) | null;
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8082';

/**
 * Relaunch guard. Stripe showed the same buyers opening three or four payment
 * pages within a minute (Sep 3 to 9 2026): this component auto-created a new
 * session on every mount, so the browser Back button from Stripe sent them
 * straight to a fresh payment page. The launch is remembered per tab and per
 * purchase; a remembered purchase waits for one click instead.
 */
const LAUNCH_MARKER_KEY = 'botspot.hostedCheckout.launched';

const SESSION_KEY = 'botspot.hostedCheckout.session';
const sessionSchema = z.object({
  checkoutUrl: z.string().refine(isStripeCheckoutUrl),
  sessionId: z.string(),
  attemptId: z.string(),
  launchKey: z.string(),
  createdAt: z.number(),
  expiresAt: z.string().nullable(),
});
type StoredSession = z.infer<typeof sessionSchema>;

function readSession(launchKey: string): StoredSession | null {
  try {
    const session = sessionSchema.parse(
      JSON.parse(window.sessionStorage.getItem(SESSION_KEY) || 'null'),
    );
    const expiresAt = session.expiresAt
      ? Date.parse(session.expiresAt)
      : session.createdAt + 24 * 60 * 60 * 1000;
    return session.launchKey === launchKey && expiresAt > Date.now() ? session : null;
  } catch {
    return null;
  }
}

function writeSession(session: StoredSession) {
  try {
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // The mounted launcher retains the URL when tab storage is unavailable.
  }
}

function readLaunchMarker(): string | null {
  try {
    return window.sessionStorage.getItem(LAUNCH_MARKER_KEY);
  } catch {
    return null;
  }
}

function writeLaunchMarker(launchKey: string) {
  try {
    window.sessionStorage.setItem(LAUNCH_MARKER_KEY, launchKey);
  } catch {
    // Private mode or blocked storage: the guard degrades to today's behavior.
  }
}

export default function HostedCheckoutLauncher({
  items,
  promoCode: promoCodeProp,
  promoEntry = false,
  returnContext,
  sourceRoute,
  flowContext,
  surface,
  assignment,
  getApiToken,
}: Props) {
  const [checkoutTheme] = useThemeMode();
  const attemptIdRef = useRef<string | null>(null);
  if (!attemptIdRef.current) attemptIdRef.current = createCheckoutAttemptId('hosted_checkout');
  const requestRef = useRef<{
    attemptId: string;
    purchaseKey: string;
    promise: ReturnType<typeof createHostedCheckoutSession>;
  } | null>(null);
  const [attemptNumber, setAttemptNumber] = useState(0);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState<string>();
  const [omitPromoCode, setOmitPromoCode] = useState(false);
  const promoCode = promoEntry ? null : promoCodeProp;
  const purchaseKey = JSON.stringify({
    items,
    promoCode: omitPromoCode ? null : promoCode,
    returnContext,
    sourceRoute,
    flowContext,
    surface,
    assignment,
  });
  // What the tab remembers: the purchase itself, not the assignment or route,
  // so a returning buyer is recognised even if the experiment re-exposes.
  const launchKey = JSON.stringify({
    items,
    promoCode: omitPromoCode ? null : promoCode,
    returnContext,
    surface,
  });
  const [awaitingResume, setAwaitingResume] = useState(
    () => !promoEntry && readLaunchMarker() === launchKey,
  );
  const [resumePending, setResumePending] = useState(false);
  const [navigationFailed, setNavigationFailed] = useState(false);
  const sessionRef = useRef<StoredSession | null>(null);
  const navigationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigatedAttempt = useRef<string | null>(null);

  useEffect(() => {
    const clearNavigationTimer = () => {
      if (navigationTimer.current) clearTimeout(navigationTimer.current);
    };
    window.addEventListener('pagehide', clearNavigationTimer);
    return () => {
      clearNavigationTimer();
      window.removeEventListener('pagehide', clearNavigationTimer);
    };
  }, []);

  const navigateToSession = useCallback(
    (session: StoredSession, outcome: 'navigating' | 'resumed') => {
      if (!isStripeCheckoutUrl(session.checkoutUrl)) return;
      if (navigationTimer.current) clearTimeout(navigationTimer.current);
      const eventProps = {
        checkout_attempt_id: session.attemptId,
        checkout_experience_assignment_id: assignment.assignmentId,
        checkout_experience_subject_id: assignment.subjectId,
        checkout_experience_surface: surface,
        checkout_experience_variant: 'stripe_hosted',
        checkout_experience_feature_flag: HOSTED_CHECKOUT_FEATURE_FLAG,
        source: sourceRoute,
        stripe_checkout_session_id: session.sessionId,
        destination_host: new URL(session.checkoutUrl).hostname,
      };
      const reportFailure = () => {
        capturePostHogEvent('checkout_experience_navigation_failed', {
          ...eventProps,
          outcome: 'navigation_failed',
        });
        setNavigationFailed(true);
        setAwaitingResume(true);
        setResumePending(false);
      };
      setNavigationFailed(false);
      capturePostHogEvent('checkout_experience_redirected', { ...eventProps, outcome });
      try {
        window.location.replace(session.checkoutUrl);
        navigationTimer.current = setTimeout(() => {
          if (/^\/checkout(?:\/|$)/.test(window.location.pathname)) reportFailure();
        }, 1200);
      } catch {
        reportFailure();
      }
    },
    [assignment.assignmentId, assignment.subjectId, sourceRoute, surface],
  );

  useEffect(() => {
    let active = true;
    if (awaitingResume) return undefined;
    if (requestRef.current && requestRef.current.purchaseKey !== purchaseKey) {
      attemptIdRef.current = createCheckoutAttemptId('hosted_checkout');
      setError('');
      setErrorCode(undefined);
    }
    const checkoutAttemptId = attemptIdRef.current;
    if (!checkoutAttemptId) return;
    const eventProps = {
      checkout_attempt_id: checkoutAttemptId,
      checkout_experience_assignment_id: assignment.assignmentId,
      checkout_experience_subject_id: assignment.subjectId,
      checkout_experience_surface: surface,
      checkout_experience_variant: 'stripe_hosted',
      checkout_experience_feature_flag: HOSTED_CHECKOUT_FEATURE_FLAG,
      source: sourceRoute,
    };
    if (requestRef.current?.attemptId !== checkoutAttemptId) {
      capturePostHogEvent('checkout_experience_session_requested', eventProps);
      requestRef.current = {
        attemptId: checkoutAttemptId,
        purchaseKey,
        promise: (async () => {
          const token = getApiToken ? await getApiToken() : null;
          return createHostedCheckoutSession({
            apiUrl: API_URL,
            token,
            checkoutAttemptId,
            items,
            promoCode: omitPromoCode ? null : promoCode,
            allowPromotionCodes: promoEntry,
            returnContext,
            attribution: {
              sourceRoute,
              flowContext,
              experimentKey: HOSTED_CHECKOUT_FEATURE_FLAG,
              variant: 'stripe_hosted',
              assignmentId: assignment.assignmentId,
              subjectId: assignment.subjectId,
              surface,
              exposedAt: assignment.exposedAt,
            },
          });
        })(),
      };
    }
    void requestRef.current.promise
      .then((result) => {
        if (!active || navigatedAttempt.current === checkoutAttemptId) return;
        navigatedAttempt.current = checkoutAttemptId;
        capturePostHogEvent('checkout_experience_session_created', {
          ...eventProps,
          stripe_checkout_session_id: result.sessionId,
        });
        // Begin checkout for the Stripe-hosted arm. The buyer reaches checkout
        // when we hand them Stripe's payment page, so this is the arrival that
        // matches the control arm's on-page arrival. Keyed on the purchase and
        // the arm, so the relaunch guard, a re-render, or a Back-button return
        // never reports a second "reached checkout".
        trackBeginCheckout({
          dedupeKey: `hosted_checkout:${surface}:${launchKey}`,
          transactionId: checkoutAttemptId,
          checkout_surface: surface,
          plan_type: items.map((item) => item.productSlug).join(','),
          plan_key: items.map((item) => item.planKey).join(','),
          cart_item_count: items.length,
          flow_context: flowContext || null,
          stripe_checkout_session_id: result.sessionId,
          ...eventProps,
        });
        const session: StoredSession = {
          checkoutUrl: result.checkoutUrl,
          sessionId: result.sessionId,
          attemptId: checkoutAttemptId,
          launchKey,
          createdAt: Date.now(),
          expiresAt: result.expiresAt,
        };
        sessionRef.current = session;
        writeSession(session);
        writeLaunchMarker(launchKey);
        navigateToSession(session, 'navigating');
      })
      .catch((launchError) => {
        if (!active) return;
        const message =
          launchError instanceof Error
            ? launchError.message
            : 'Secure checkout could not be opened. Please try again.';
        setError(message);
        setResumePending(false);
        setErrorCode(launchError instanceof HostedCheckoutError ? launchError.code : undefined);
        capturePostHogEvent('checkout_experience_launch_failed', {
          ...eventProps,
          error_message: message,
        });
      });
    return () => {
      active = false;
    };
  }, [
    assignment,
    attemptNumber,
    awaitingResume,
    launchKey,
    flowContext,
    getApiToken,
    items,
    omitPromoCode,
    promoCode,
    promoEntry,
    purchaseKey,
    navigateToSession,
    returnContext,
    sourceRoute,
    surface,
  ]);

  const resume = () => {
    if (resumePending) return;
    setResumePending(true);
    const session = readSession(launchKey) || sessionRef.current;
    const expiresAt = session?.expiresAt
      ? Date.parse(session.expiresAt)
      : (session?.createdAt || 0) + 24 * 60 * 60 * 1000;
    if (
      session &&
      session.launchKey === launchKey &&
      isStripeCheckoutUrl(session.checkoutUrl) &&
      expiresAt > Date.now()
    ) {
      navigateToSession(session, 'resumed');
      return;
    }
    setNavigationFailed(false);
    attemptIdRef.current = createCheckoutAttemptId('hosted_checkout');
    setAwaitingResume(false);
  };

  const retry = (removePromo = false) => {
    attemptIdRef.current = createCheckoutAttemptId('hosted_checkout');
    if (removePromo) setOmitPromoCode(true);
    setError('');
    setErrorCode(undefined);
    setAttemptNumber((value) => value + 1);
  };

  return (
    <main className="checkout-page bsui-scope" data-bs-theme={checkoutTheme} aria-live="polite">
      <div className="checkout-loading hosted-checkout-status">
        {error ? (
          <>
            <h1>Secure checkout could not open</h1>
            <p role="alert">{error}</p>
            <button type="button" className="btn btn-primary" onClick={() => retry()}>
              Try again
            </button>
            {errorCode === 'INVALID_PROMOTION' && promoCode && !omitPromoCode && (
              <button type="button" className="btn btn-secondary" onClick={() => retry(true)}>
                Continue without promo code
              </button>
            )}
          </>
        ) : awaitingResume || resumePending ? (
          <>
            <h1>Your order is ready</h1>
            {navigationFailed ? (
              <p role="alert">
                Secure checkout could not open. Continue to try your saved checkout again.
              </p>
            ) : (
              <p>
                You previously opened Stripe&apos;s secure checkout. Continue when you are ready.
              </p>
            )}
            <button
              type="button"
              className="btn btn-primary"
              onClick={resume}
              disabled={resumePending}
              aria-busy={resumePending}
            >
              Continue To Secure Checkout
            </button>
          </>
        ) : (
          <>
            <Spinner animation="border" />
            <span>Opening secure Stripe checkout…</span>
          </>
        )}
      </div>
    </main>
  );
}
